import React, { useState, useMemo } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import JournalEntryModal from './JournalEntryModal';
import { buildJournalFormData } from '../../utils/buildJournalFormData';

const ChargeCapturePanel = ({
  captureData = [],
  setActiveInfoItem,
  visit,
  data,
  journalIndex,
  refreshVisitDetails,
}) => {
  const [openJournal, setOpenJournal] = useState(false);
  const [journalState, setJournalState] = useState(null);

  /* --------------------------------
     Helpers
  -------------------------------- */

  const doesJournalExist = (item) => {
    if (!item) return false;
  
    // Medical note logic (keep existing behavior)
    if (isMedicalNoteCheck(item)) {
      const key = (item.rule_id ?? '').toUpperCase();
      return journalIndex?.byInvoiceItemId?.has(key);
    }
    
    // Charge capture logic (new, simplified)
    if (typeof item.title === 'string') {
      return journalIndex?.byLabel?.has(item.title.trim());
    }

    return false;
  };
  
  const isMedicalNoteCheck = item =>
    typeof item?.rule_id === 'string' && item.rule_id.startsWith('mn');

  const findMatchingInvoiceLine = (item) => {
    if (!item?.codes_to_check_for || !Array.isArray(data)) return null;

    const codes = item.codes_to_check_for
      .split(',')
      .map(c => c.trim().toUpperCase());

    return data.find(row =>
      codes.some(code =>
        row?.pims_id?.toUpperCase().includes(code)
      )
    ) ?? null;
  };

  /* --------------------------------
     Sort FAIL first
  -------------------------------- */

  const sortedCaptureData = useMemo(() => (
    [...captureData].sort((a, b) => {
      if (a.result !== b.result) return a.result === 'FAIL' ? -1 : 1;
      return (a.priority || 0) - (b.priority || 0);
    })
  ), [captureData]);

  /* --------------------------------
     Journal click
  -------------------------------- */

  const handleJournalClick = (item) => {
    const isMedicalNote = isMedicalNoteCheck(item);

    const built = buildJournalFormData({
      journalType: isMedicalNote ? 'medical_note' : 'charge_capture',
      visit,
      chargeCaptureItem: item,
      matchedInvoiceLine: isMedicalNote
        ? null
        : findMatchingInvoiceLine(item),
    });

    setJournalState(built);
    setOpenJournal(true);
  };

  const handleCloseJournal = () => {
    setOpenJournal(false);
    setJournalState(null);
  };

  /* --------------------------------
     Render
  -------------------------------- */

  return (
    <>
      <div className="charge-capture-list">
        {sortedCaptureData.map((item, idx) => (
          <div
            key={idx}
            className="charge-capture-card"
            style={{
              borderLeft: `4px solid ${item.color}`,
              padding: '8px 12px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            {/* Left */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong>{item.title}</strong>
              <span
                className={item.result === 'FAIL' ? 'fail' : 'pass'}
                style={{ marginLeft: 8 }}
              >
                {item.result}
              </span>
            </div>

            {/* Right */}
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <Tooltip title="More Info">
                <IconButton size="small" onClick={() => setActiveInfoItem(item)}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {item.result === 'FAIL' && (() => {
                const journalExists = doesJournalExist(item);

                return (
                  <Tooltip
                    title={journalExists ? 'Journal already exists' : 'Journal Entry'}
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={
                          journalExists
                            ? undefined
                            : () => handleJournalClick(item)
                        }
                        sx={{
                          padding: '2px',
                          width: '24px',
                          height: '24px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          backgroundColor: '#f5f5f5',
                          pointerEvents: journalExists ? 'none' : 'auto',
                          cursor: journalExists ? 'default' : 'pointer',
                          '&:hover': {
                            backgroundColor: '#e5e7eb',
                          },
                        }}
                      >
                        {journalExists ? (
                          <CheckCircleIcon
                            fontSize="small"
                            className="icon-completed"
                          />
                        ) : (
                          <EditIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                );
              })()}
            </div>
          </div>
        ))}
      </div>

      <JournalEntryModal
        open={openJournal}
        onClose={handleCloseJournal}
        onSuccess={handleCloseJournal}
        journal={journalState}
        refreshVisitDetails={refreshVisitDetails}       
      />
    </>
  );
};

export default ChargeCapturePanel;